import { useMemo } from 'react';
import styled, { css } from 'foundations-theming/utils';
import EPDragHandle from 'editor-components/sidebar/addableComponents/EPDragHandle';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import SidebarTooltip from 'LayoutBuilderUI/containers/SidebarTooltip';
import ModuleRibbon from 'LayoutBuilderUI/components/extras/ModuleRibbon';
import { TILE_SIZE_PX, TILE_MAX_CHAR_COUNT } from 'LayoutBuilderUI/components/tiles/tileUtils';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Tile = styled.div.withConfig({
  displayName: "DraggableTile__Tile"
})(({
  tileSize,
  disabled,
  isLocked,
  theme: _theme
}) => {
  const theme = _theme;
  const backgroundColor = disabled ? 'fill-primary-disabled' : 'fill-surface-default';
  return css(["width:", ";height:", ";display:flex;flex-direction:column;align-items:center;justify-content:space-between;background-color:", ";border-color:", ";border-radius:", ";border-width:", ";border-style:solid;position:relative;color:", ";padding-block:", ";", " &:hover{cursor:", ";}"], TILE_SIZE_PX[tileSize], TILE_SIZE_PX[tileSize], theme.color[backgroundColor], theme.color['border-tertiary-default'], theme.borderRadius[200], theme.borderWidth[100], disabled ? theme.color['text-tertiary-disabled'] : 'inherit', theme.space[200], !disabled && css(["box-shadow:", ";&:hover{border-color:", ";}"], theme.boxShadow[100], theme.color['border-core-hover']), disabled && !isLocked ? 'not-allowed' : 'grab');
});
export const Label = styled.small.withConfig({
  displayName: "DraggableTile__Label"
})(({
  theme: _theme
}) => {
  const theme = _theme;
  return {
    fontSize: theme.typography['detail-200'].fontSize
  };
});
const useTooltipConfig = ({
  label,
  tileSize,
  isLocked,
  disabled,
  disabledMessage
}) => {
  return useMemo(() => {
    const isSmallTileWithLongLabel = tileSize === 'small' && label.length >= TILE_MAX_CHAR_COUNT.small;
    const isMediumTileWithLongLabel = tileSize === 'medium' && label.length >= TILE_MAX_CHAR_COUNT.medium;
    const isTileDisabled = disabled && disabledMessage && !isLocked;
    let tooltipMessage;
    if (isTileDisabled) {
      tooltipMessage = disabledMessage;
    } else if (isSmallTileWithLongLabel) {
      tooltipMessage = label;
    } else if (isMediumTileWithLongLabel) {
      tooltipMessage = label;
    }
    return {
      disabled: !isSmallTileWithLongLabel && !isTileDisabled && !isMediumTileWithLongLabel,
      tooltipMessage
    };
  }, [label, tileSize, isLocked, disabled, disabledMessage]);
};
function DraggableTile({
  tileSize = 'small',
  'data-test-id': dataTestId,
  'data-module-name': moduleName,
  'data-module-id': moduleId,
  innerRef,
  disabledMessage,
  icon,
  label,
  tooltipPlacement,
  disabled = false,
  isNew,
  isBeta,
  isLocked,
  tooltipTitle,
  isEnabledForEmailV3Rendering,
  onDoubleClick,
  onClick
}) {
  let tileStatus = 'enabled';
  if (disabled) {
    tileStatus = 'disabled';
  }
  if (isLocked) {
    tileStatus = 'locked';
  }
  const tooltip = useTooltipConfig({
    label,
    tileSize,
    isLocked,
    disabled,
    disabledMessage
  });
  return /*#__PURE__*/_jsx("div", {
    ref: innerRef,
    children: /*#__PURE__*/_jsx(SidebarTooltip, {
      title: tooltip.tooltipMessage,
      disabled: tooltip.disabled,
      placement: "top left",
      children: /*#__PURE__*/_jsxs(Tile, {
        tileSize: tileSize,
        disabled: disabled,
        isLocked: isLocked,
        "data-test-id": dataTestId,
        "data-module-name": moduleName,
        "data-module-id": moduleId,
        onDoubleClick: onDoubleClick,
        onClick: onClick,
        "data-tile-status": tileStatus,
        children: [/*#__PURE__*/_jsx(ModuleRibbon, {
          isNew: isNew,
          isBeta: isBeta,
          isLocked: isLocked,
          isEnabledForEmailV3Rendering: isEnabledForEmailV3Rendering,
          tooltipTitle: tooltipTitle,
          tooltipPlacement: tooltipPlacement
        }), /*#__PURE__*/_jsxs(View, {
          flexBasis: "20%",
          children: [/*#__PURE__*/_jsx(EPDragHandle, {
            color: "icon-core-subtle"
          }), isLocked && /*#__PURE__*/_jsx(IconHolder, {
            icon: LockedIcon,
            size: "100",
            color: "icon-primary-disabled",
            position: "absolute",
            insetBlockStart: "-3px",
            insetInlineEnd: "0"
          })]
        }), /*#__PURE__*/_jsx(Flex, {
          "data-test-id": "module-tile-icon",
          marginInline: "auto",
          flexBasis: "40%",
          minInlineSize: "75%",
          justifyContent: "center",
          paddingBlockStart: "200",
          children: icon
        }), /*#__PURE__*/_jsx(Flex, {
          flexBasis: "40%",
          maxInlineSize: "100%",
          inlineSize: "100%",
          paddingInline: "100",
          justifyContent: "center",
          alignItems: "center",
          children: /*#__PURE__*/_jsx(TruncateMultiline, {
            textAlign: "center",
            Paragraph: Label,
            maxChars: TILE_MAX_CHAR_COUNT[tileSize],
            maxInlineSize: "100%",
            text: label,
            display: "inline-block",
            overflowWrap: "break-word"
          })
        })]
      })
    })
  });
}
export default DraggableTile;